import { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { updateSessionNote } from "../lib/api";
import { toast } from "sonner";

const KEY = (pid, sid) => `trace_note_asked_${pid}_${sid}`;

/**
 * Habit hook — when the user has been active on a project for a bit and then
 * either (a) goes idle for IDLE_MS, or (b) tab becomes hidden, we surface a
 * gentle "What were you working on?" prompt for the latest session.
 *
 * Once the user saves or dismisses for a given session, we don't re-ask in
 * the same browser.
 */
const IDLE_MS = 7 * 1000; // 7s idle — short enough to catch a real pause
const ARM_AFTER_MS = 20 * 1000; // wait at least 20s on page before arming

export default function HabitNotePrompt({ projectId, session, onSaved }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(session?.session_note || "");
  const [saving, setSaving] = useState(false);
  const armedAtRef = useRef(Date.now() + ARM_AFTER_MS);
  const idleTimer = useRef(null);
  const triggeredRef = useRef(false);

  // Skip entirely if no session or already noted or already asked
  const sessionId = session?.id;
  const alreadyAsked =
    sessionId && typeof window !== "undefined" && localStorage.getItem(KEY(projectId, sessionId));
  const hasNote = !!session?.session_note?.trim();
  const skip = !sessionId || hasNote || alreadyAsked;

  // Sync local note when session changes
  useEffect(() => {
    setNote(session?.session_note || "");
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Idle / visibility detection
  useEffect(() => {
    if (skip) return;

    const trigger = () => {
      if (triggeredRef.current) return;
      if (Date.now() < armedAtRef.current) return;
      triggeredRef.current = true;
      setOpen(true);
    };

    const resetIdle = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(trigger, IDLE_MS);
    };

    const onVis = () => {
      if (document.visibilityState === "hidden") {
        trigger();
      } else {
        resetIdle();
      }
    };
    const onBlur = () => trigger();

    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach((e) => window.addEventListener(e, resetIdle, { passive: true }));
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    resetIdle();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetIdle));
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [skip, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (skip) return null;

  const dismiss = () => {
    localStorage.setItem(KEY(projectId, sessionId), "dismissed");
    setOpen(false);
  };

  const save = async () => {
    if (!note.trim()) {
      dismiss();
      return;
    }
    setSaving(true);
    try {
      await updateSessionNote(projectId, sessionId, note.trim());
      localStorage.setItem(KEY(projectId, sessionId), "saved");
      toast.success("Saved. Future-you will thank you.");
      setOpen(false);
      onSaved?.();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="habit-prompt"
      className="fixed bottom-6 right-6 z-40 w-[340px] rounded-2xl bg-white border border-[#e5e7eb] shadow-[0_18px_50px_rgba(17,24,39,0.12)] p-5 trace-fade-in"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#6b7280]">
          <Sparkles size={12} className="text-indigo-500" />
          One quick note
        </div>
        <button
          aria-label="Dismiss"
          onClick={dismiss}
          className="text-[#9ca3af] hover:text-[#111827] transition"
          data-testid="habit-dismiss"
        >
          <X size={14} />
        </button>
      </div>
      <h4 className="mt-2 text-[15px] font-semibold text-[#111827]">
        What were you trying to finish?
      </h4>
      <p className="mt-1 text-[12px] text-[#6b7280] leading-relaxed">
        One line so future-you knows the intent — not just the files.
      </p>
      <textarea
        data-testid="habit-input"
        autoFocus
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. Fixing webhook retry logic"
        className="mt-3 w-full text-sm bg-[#f7f8fa] border border-[#e5e7eb] rounded-lg px-3 py-2 outline-none focus:border-indigo-400 transition resize-none"
      />
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          onClick={dismiss}
          className="text-xs text-[#6b7280] hover:text-[#111827] transition px-2 py-1"
        >
          Not now
        </button>
        <button
          data-testid="habit-save"
          disabled={saving}
          onClick={save}
          className="px-3 py-1.5 rounded-md bg-[#111827] text-white text-xs hover:bg-[#374151] transition disabled:opacity-60"
        >
          Save
        </button>
      </div>
    </div>
  );
}
